import { Helmet } from 'react-helmet-async';
import type { Screen } from '../types';

interface SEOProps {
  screen?: Screen;
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  match?: {
    homeTeam: string;
    awayTeam: string;
    matchTime: string;
  };
}

const defaultTitle = 'AFCON Predict - Predict Africa Cup of Nations 2025 Match Results';
const defaultDescription = 'Predict AFCON 2025 match results, compete with friends in custom squads, and track your performance on the global leaderboard. Join thousands of football fans making predictions!';
const defaultKeywords = 'AFCON, Africa Cup of Nations, football predictions, match predictions, AFCON 2025, football app, prediction game, squad competition, leaderboard';
const baseUrl = 'https://afconpredict.com';
const defaultImage = `${baseUrl}/logo.png`;

export function SEO({
  screen = 'landing',
  title,
  description,
  keywords,
  image = defaultImage,
  url,
  match,
}: SEOProps) {
  // Generate dynamic content based on screen
  let finalTitle = title || defaultTitle;
  let finalDescription = description || defaultDescription;
  let finalKeywords = keywords || defaultKeywords;
  let finalUrl = url || baseUrl;

  switch (screen) {
    case 'predictor':
      if (match) {
        finalTitle = title || `${match.homeTeam} vs ${match.awayTeam} - Predict Match Result | AFCON Predict`;
        finalDescription = description || `Predict the result of ${match.homeTeam} vs ${match.awayTeam} in AFCON 2025. Make your prediction and compete on the leaderboard!`;
        finalKeywords = keywords || `AFCON 2025, ${match.homeTeam}, ${match.awayTeam}, match prediction, football prediction`;
      } else {
        finalTitle = title || 'Predict Match Results - AFCON Predict';
        finalDescription = description || 'Make your predictions for AFCON 2025 matches and compete with friends!';
      }
      break;
    case 'leaderboard':
      finalTitle = title || 'Global Leaderboard - AFCON Predict';
      finalDescription = description || 'View the global leaderboard and see how you rank against other AFCON 2025 prediction players. Track your performance and compete for the top spot!';
      finalKeywords = keywords || 'AFCON leaderboard, prediction rankings, football leaderboard, AFCON 2025 rankings';
      break;
    case 'about':
      finalTitle = title || 'About AFCON Predict - Football Prediction App';
      finalDescription = description || 'Learn about AFCON Predict, the ultimate app for predicting Africa Cup of Nations 2025 match results and competing with friends.';
      break;
    case 'faq':
      finalTitle = title || 'Frequently Asked Questions - AFCON Predict';
      finalDescription = description || 'Find answers to common questions about AFCON Predict, how to make predictions, join squads, and compete on the leaderboard.';
      break;
    case 'privacy':
      finalTitle = title || 'Privacy Policy - AFCON Predict';
      finalDescription = description || 'Read our privacy policy to understand how AFCON Predict collects, uses, and protects your personal information.';
      break;
  }

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="title" content={finalTitle} />
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={finalUrl} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={finalTitle} />
      <meta property="og:site_name" content="AFCON Predict" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={finalUrl} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={finalTitle} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={finalUrl} />
      
      {/* Structured Data */}
      {screen === 'landing' && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebApplication',
            name: 'AFCON Predict',
            description: finalDescription,
            url: baseUrl,
            applicationCategory: 'SportsApplication',
            operatingSystem: 'Web',
            offers: {
              '@type': 'Offer',
              price: '0',
              priceCurrency: 'USD',
            },
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: '4.8',
              ratingCount: '1000',
            },
            screenshot: image,
          })}
        </script>
      )}
      
      {screen === 'predictor' && match && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'SportsEvent',
            name: `${match.homeTeam} vs ${match.awayTeam}`,
            description: `AFCON 2025 match between ${match.homeTeam} and ${match.awayTeam}`,
            startDate: match.matchTime,
            sport: 'Football',
            eventStatus: {
              '@type': 'EventScheduled',
            },
            location: {
              '@type': 'Place',
              name: 'Africa Cup of Nations 2025',
            },
          })}
        </script>
      )}
    </Helmet>
  );
}

